import { type FC, type MouseEvent, memo } from "react";
import type { GapSegment as GapSegmentType } from "$/modules/segmentation/utils/segment-processing.ts";
import { useAtomValue, useSetAtom } from "jotai";
import {
	timelineDragAtom,
} from "$/modules/spectrogram/states/dnd.ts";
import {
	selectedLinesAtom,
	selectedWordsAtom,
} from "$/states/main.ts";
import {
	spectrogramFontSizeAtom,
	spectrogramAutoAdjustFontAtom,
	spectrogramLyricMaxCharsAtom,
} from "$/modules/spectrogram/states/index.ts";
import { selectionPanOffsetAtom } from "$/modules/spectrogram/states/dnd.ts";
import styles from "./GapSegment.module.css";
import wordStyles from "./LyricWordSegment.module.css";

interface GapSegmentProps {
	lineId: string;
	segment: GapSegmentType;
	lineStartTime: number;
	zoom: number;
	text?: string;
}

export const GapSegment: FC<GapSegmentProps> = memo(({ lineId, segment, lineStartTime, zoom, text }) => {
	const setTimelineDrag = useSetAtom(timelineDragAtom);
	const setSelectedLines = useSetAtom(selectedLinesAtom);
	const setSelectedWords = useSetAtom(selectedWordsAtom);
	const selectedLines = useAtomValue(selectedLinesAtom);
	const baseFontSize = useAtomValue(spectrogramFontSizeAtom);
	const autoAdjustFont = useAtomValue(spectrogramAutoAdjustFontAtom);
	const timelineDragAtomValue = useAtomValue(timelineDragAtom);
	const selectionPanOffset = useAtomValue(selectionPanOffsetAtom);
	const lyricMaxChars = useAtomValue(spectrogramLyricMaxCharsAtom);

	const displayText = text && lyricMaxChars > 0 && text.length > lyricMaxChars
		? text.slice(0, lyricMaxChars) + "\u2026"
		: text;

	const { startTime, endTime } = segment;

	if (startTime == null || endTime == null || endTime <= startTime) {
		return null;
	}

	const left = ((startTime - lineStartTime) / 1000) * zoom;
	const width = ((endTime - startTime) / 1000) * zoom;

	if (width < 1) {
		return null;
	}

	let actualFontSize = baseFontSize;
	if (displayText && autoAdjustFont) {
		let ems = 0;
		for (let i = 0; i < displayText.length; i++) {
			ems += displayText.charCodeAt(i) > 255 ? 1 : 0.55;
		}
		const maxPx = (width - 12) / Math.max(ems, 1);
		actualFontSize = Math.min(baseFontSize, Math.max(8, maxPx));
	}

	const isCurrentlySelected = text ? selectedLines.has(lineId) : false;
	const isSelectedByPan = isCurrentlySelected && timelineDragAtomValue?.type === "selection-pan";
	const effectiveLeft = isSelectedByPan ? left + (selectionPanOffset * zoom) / 1000 : left;

	const dynamicStyles = {
		left: `${effectiveLeft}px`,
		width: `${width}px`,
		backgroundColor: isCurrentlySelected ? "var(--accent-a6)" : "transparent",
	};

	const handlePanStart = (e: MouseEvent<HTMLDivElement>) => {
		if (e.button !== 0 || !text) return;
		e.preventDefault();
		e.stopPropagation();

		if (!isCurrentlySelected) {
			setSelectedLines(new Set([lineId]));
			setSelectedWords(new Set());
		}

		setTimelineDrag({
			type: "selection-pan",
			startX: e.clientX,
		});
	};

	const handleClick = (e: MouseEvent<HTMLDivElement>) => {
		if (!text) return;
		e.stopPropagation();
		if (e.shiftKey) {
			setSelectedLines((prev) => {
				const newSet = new Set(prev);
				newSet.add(lineId);
				return newSet;
			});
		} else {
			setSelectedLines(new Set([lineId]));
		}
		setSelectedWords(new Set());
	};

	return (
		// biome-ignore lint/a11y/useSemanticElements: This is an interactive timeline element
		<div
			className={text ? wordStyles.wordSegment : styles.gapSegment}
			style={dynamicStyles}
			onMouseDown={text ? handlePanStart : undefined}
			onClick={text ? handleClick : undefined}
			role={text ? "button" : undefined}
			tabIndex={text ? 0 : undefined}
		>
			{displayText && <span className={wordStyles.originText} style={{ fontSize: `${actualFontSize}px` }}>{displayText}</span>}
		</div>
	);
});
